import { BookOpenText, GraduationCap, Layers3 } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { CrudWorkspace } from '@/components/shared/crud-workspace'
import { MetricCard } from '@/components/shared/metric-card'
import { useFirestoreCrud } from '@/hooks/useFirestoreCrud'
import { omitEmptyValues, stringToBoolean, stringToNumber } from '@/lib/cms-form'
import { formatCompactNumber } from '@/lib/format'
import { ContentArticle, ContentCategory, ContentClass } from '@/types'

export function ContentPage() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const tab = params.get('tab') || 'categories'

  const articles = useFirestoreCrud<ContentArticle>('articles')
  const classes = useFirestoreCrud<ContentClass>('classes')
  const categories = useFirestoreCrud<ContentCategory>('contentCategories')
  const contentCategoryOptions = categories.items
    .map((item) => item.name)
    .filter(Boolean) as string[]

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Artikel"
          value={formatCompactNumber(articles.items.length)}
          description="Konten editorial, news, health tips, dan landing artikel."
          icon={BookOpenText}
        />
        <MetricCard
          title="Kelas"
          value={formatCompactNumber(classes.items.length)}
          description="Program kelas, workshop, atau coaching."
          icon={GraduationCap}
          tone="amber"
        />
        <MetricCard
          title="Kategori"
          value={formatCompactNumber(categories.items.length)}
          description="Kategori untuk artikel dan kelas."
          icon={Layers3}
          tone="slate"
        />
      </div>

      {(tab === 'articles') && (
        <CrudWorkspace<ContentArticle>
          description="Manajemen artikel edukasi, panduan kesehatan, dan konten landing."
          emptyDescription="Belum ada artikel. Tambahkan artikel pertama untuk ekosistem Parlent."
          emptyTitle="Artikel kosong"
          enableSearchAndPagination
          searchPlaceholder="Cari artikel, kategori, atau slug..."
          fields={[
            { key: 'imageUrl', label: 'Cover Image', type: 'image' },
            { key: 'title', label: 'Judul Artikel', placeholder: 'Tips Lari Pagi untuk Pemula' },
            { key: 'slug', label: 'Slug / URL Key', placeholder: 'tips-lari-pagi' },
            { key: 'category', label: 'Kategori', type: 'select', options: contentCategoryOptions },
            { key: 'summary', label: 'Ringkasan / Deskripsi', type: 'textarea', placeholder: 'Deskripsi singkat artikel...' },
            { key: 'content', label: 'Konten Lengkap', type: 'richtext', placeholder: 'Isi artikel lengkap...' },
            { key: 'actionUrl', label: 'CTA / Redirect URL', placeholder: 'https://...' },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['draft', 'published', 'archived'],
            },
            { key: 'featured', label: 'Featured (Show in Hero)', type: 'boolean' },
          ]}
          items={articles.items}
          onDelete={articles.remove}
          onSave={(data) => {
            const payload = omitEmptyValues({
              id: data.id,
              imageUrl: data.imageUrl,
              title: data.title,
              slug: data.slug,
              category: data.category,
              summary: data.summary,
              content: data.content,
              actionUrl: data.actionUrl,
              status: data.status || 'draft',
              featured: stringToBoolean(data.featured),
            })
            void articles.upsert(payload)
          }}
          title="Artikel"
        />
      )}

      {(tab === 'classes') && (
        <CrudWorkspace<ContentClass>
          description="Manajemen kelas online, coaching, challenge, atau workshop komunitas."
          emptyDescription="Belum ada kelas. Tambahkan kelas pertama Parlent."
          emptyTitle="Kelas kosong"
          enableSearchAndPagination
          searchPlaceholder="Cari kelas, mentor, kategori, atau level..."
          fields={[
            { key: 'imageUrl', label: 'Poster Image', type: 'image' },
            { key: 'title', label: 'Nama Kelas', placeholder: 'Yoga Basics with Parlent' },
            { key: 'category', label: 'Kategori', type: 'select', options: contentCategoryOptions },
            { key: 'mentor', label: 'Mentor / Coach', placeholder: 'Coach Sarah' },
            { key: 'description', label: 'Deskripsi Singkat', type: 'textarea' },
            { key: 'content', label: 'Detail & Syarat', type: 'richtext' },
            { key: 'duration', label: 'Durasi (Menit)', type: 'number', placeholder: '45' },
            { 
              key: 'level', 
              label: 'Tingkat Kesulitan', 
              type: 'select', 
              options: ['All Level', 'Beginner', 'Intermediate', 'Advanced'] 
            },
            { key: 'youtubeUrl', label: 'YouTube Stream URL', placeholder: 'https://youtube.com/watch?v=...' },
            { key: 'joinUrl', label: 'Registration / Join URL', placeholder: 'https://...' },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['draft', 'published', 'closed'],
            },
            { key: 'featured', label: 'Featured (Show in Banner)', type: 'boolean' },
          ]}
          items={classes.items}
          onDelete={classes.remove}
          onSave={(data) => {
            const payload = omitEmptyValues({
              id: data.id,
              imageUrl: data.imageUrl,
              title: data.title,
              category: data.category,
              mentor: data.mentor,
              description: data.description,
              content: data.content,
              duration: stringToNumber(data.duration),
              level: data.level || 'All Level',
              youtubeUrl: data.youtubeUrl,
              joinUrl: data.joinUrl,
              actionUrl: data.joinUrl || data.actionUrl,
              status: data.status || 'draft',
              featured: stringToBoolean(data.featured),
            })
            void classes.upsert(payload)
          }}
          title="Kelas"
        />
      )}

      {(tab === 'categories') && (
        <CrudWorkspace<ContentCategory>
          description="Kelola kategori konten agar artikel dan kelas lebih mudah dikelompokkan."
          emptyDescription="Belum ada kategori konten."
          emptyTitle="Kategori kosong"
          fields={[
            { key: 'name', label: 'Nama Kategori', placeholder: 'Wellness / Challenge / Event' },
            { key: 'slug', label: 'Slug Kategori', placeholder: 'wellness' },
            { key: 'description', label: 'Deskripsi', type: 'textarea', placeholder: 'Digunakan untuk pengelompokan artikel dan kelas.' },
            {
              key: 'type',
              label: 'Tipe',
              type: 'select',
              options: ['article', 'class', 'shared'],
            },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['active', 'inactive'],
            },
          ]}
          items={categories.items}
          onDelete={categories.remove}
          onReset={categories.reset}
          onSave={(data) => {
            const payload = omitEmptyValues({
              id: data.id,
              name: data.name,
              slug: data.slug,
              description: data.description,
              type: data.type || 'shared',
              status: data.status || 'active',
            })
            void categories.upsert(payload)
          }}
          title="Kategori Konten"
        />
      )}
    </div>
  )
}
